import { useContext, useState } from "react"
import { Chip } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  LessonStudentsList, 
  IntegrationTypeBadge, 
  MisBadge, 
  MisCountBadge, 
  Page, 
  PageHeader,
  SiteDataMenu,
} from "@components"

import { useLazyQuery, useQuery } from "@apollo/client"

import { 
  GET_LESSONS, 
  GET_SITE_DATA_LESSONS_SUMMARY, 
  GET_SITE_DATA_LESSONS_XLS_DOWNLOAD_LINK 
} from "@graphql"

import { ContextualSidebarContext, UserContext } from "@context"

import { 
  downloadFile, 
  lastImportDuration, 
  mySqlToLocal, 
  showLoader,
  staffFullname 
} from "@helpers"

import {   
  useColumnManager,
  useSetActiveSidebarLink 
} from "@hooks"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { Stats } from "./Stats"

const Lessons = () => {
  const { user } = useContext(UserContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(Object.keys(filters))
  const [searchTerm, setSearchTerm] = useState("")
  const [sortDescriptor, setSortDescriptor] = useState({ column: "name", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  useSetActiveSidebarLink("SITE_DATA_LESSONS", "SITE_DATA")
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "data.lessons.columns", defaultColumns)

  const summaryData = useQuery(GET_SITE_DATA_LESSONS_SUMMARY, {
    variables: {
      clientId: user.me.client.id,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_LESSONS, {
    variables: {
      searchTerm,
      page,
      perPage,
      clientId: user.me.client.id,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      where: selectedFilters.length < 2 ? { column: "IS_SYNC", value: selectedFilters.includes("sync") } : null,
    },

    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const [getSiteDataLessonsXlsDownloadLink] = useLazyQuery(GET_SITE_DATA_LESSONS_XLS_DOWNLOAD_LINK, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.dataLessonsXlsDownload.filename, data.dataLessonsXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSiteDataLessonsXlsDownloadLink()
    }
  }

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetch().then(() => setRefetching(false))
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowStudents = lesson =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: lesson.name,
      subTitle: `${lesson.name} students are listed below.`,
      content: <LessonStudentsList lesson={lesson} />,
      size: "lg",
    }))

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilters => {
    setSelectedFilters(Object.keys(filters).filter(filter => selectedFilters.has(filter)))
  }

  const renderCell = (lesson, column) => {
    if (column === "actions") {
      const items = [{ id: 1, handleClick: () => handleShowStudents(lesson), caption: "View Students" }]

      return <EllipsisDropdown items={items} />
    }

    if (["created_at", "updated_at"].includes(column)) {
      return mySqlToLocal(lesson[column]).format("YYYY-MM-DD HH:mm")
    }

    if (column === "teacher") {
      return staffFullname(lesson?.teacher) || "N/A"
    }

    if (column === "subject") {
      return lesson?.subject?.name || "N/A"
    }

    if (column === "is_sync" && lesson.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync", content: "text-white" }} size="sm" variant="flat">
          Sync
        </Chip>
      )
    }

    if (column === "is_sync" && !lesson.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white" }} size="sm" variant="flat">
          Custom
        </Chip>
      )
    }

    return lesson[column]
  }

  if (error) return `Error! ${error.message}`

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.client?.lessons?.data, refetching);

  return (
    <Page>
      <PageHeader
        title="Lesson Data"
        subTitle="Imported Lesson data from your MIS / CSV file."
        icon={<CircleStackIcon className="page-header-icon" />}
        contentBottom={
          <div className="flex flex-wrap gap-2">
            <IntegrationTypeBadge name={user?.me?.client?.importType?.name || "unknown"} />
            <MisBadge name={user?.me?.client?.mis || "n/a"} />
            <MisCountBadge name={`${summaryData?.data?.dataLessonsSummary?.total || 0} Lessons`} />
          </div>
        }
        actions={<SiteDataMenu clientId={user.me.client.id} />}
      />

      <Stats
        summary={{
          dataAge: summaryData?.data?.dataLessonsSummary?.data_age,
          lastImport: lastImportDuration(summaryData?.data?.dataLessonsSummary?.imported_at),
          total: summaryData?.data?.dataLessonsSummary?.total,
          syncCount: summaryData?.data?.dataLessonsSummary?.sync_count,
          customCount: summaryData?.data?.dataLessonsSummary?.custom_count,
          missingTeacherCount: summaryData?.data?.dataLessonsSummary?.missing_teacher_count,
        }}
      />

      <DataTable
        columns={columns}
        currentPage={page}
        currentSortOrder={sortDescriptor}
        data={data?.client?.lessons?.data}
        downloadOptions={downloadOptions}
        filters={filters}
        handleDownload={type => handleDownload(type)}
        handlePageChange={page => setPage(page)}
        handleRefetch={() => handleRefetchButtonClicked()}
        handleRenderCell={(item, key) => renderCell(item, key)}
        handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
        handleSearchClear={handleSearchClear}
        handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
        handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
        isDownloading={isDownloading}
        loading={showLoadingPlaceholder}
        isRefetching={refetching}
        noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Lessons Found" description="No Lessons found! Try clearing any filters." />}
        paginatorInfo={data?.client?.lessons?.paginatorInfo}
        searchInputPlaceHolder="Search by name / teacher / subject"
        searchTerm={searchTerm}
        selectedColumns={selectedColumns}
        selectedFilters={selectedFilters}
        showDownloadButton={true}
        showRefetchButton={true}
      />
    </Page>
  )
}

export default Lessons
